/* eslint-disable react/prop-types */
/*
 * PÁGINA DE PRUEBA — BORRAR DESPUÉS.
 * Combina el Configurador de Pólizas de Carga Externa con los datos reales del reporte de
 * conciliación (descuentos + PATRONAL/VIVIENDA) para armar el DEBE/HABER por grupo de empleado,
 * con el mismo formato de las hojas IPEJAL/SEDAR del documento de requerimientos (ficha 170).
 * No es codigo definitivo: es para verificar visualmente que el Configurador ya construido
 * produce un resultado correcto antes de decidir si se formaliza como funcionalidad real.
 */
import React, { useState, useEffect } from 'react';
import {
  Row, Col, Button, Spin, Table as AntTable, Typography, message, Card, Result,
} from 'antd';
import Select from 'components/Select';
import API from 'utils/api';
import { onError } from 'utils/handlers';
import { getExternaDescuentos } from 'api/catalogos/CargaExternaDescuento';
import { getReporteIPEJALPatronalVivienda } from 'api/nominas/reporteIPEJALPatronalVivienda';
import { clasificarGrupoIPEJAL } from 'utils/clasificacionIPEJAL';
import { getConfiguracionesDePolizasDeCargaExterna } from 'api/nominas/configuracionesDePolizasDeCargaExterna';
import { getPolizasDeCargaExterna } from 'api/nominas/polizasDeCargaExterna';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import currencyFormatter from 'utils/currencyFormatter';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';

const { Title, Text } = Typography;

const normalize = (str) => (str || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .trim();

const GRUPOS = [
  { key: 'plantilla', label: 'PLANTILLA' },
  { key: 'seguridad', label: 'COMISARIA (SEGURIDAD)' },
  { key: 'eventual', label: 'EVENTUAL' },
];

// PRUEBA - BORRAR DESPUES: mientras esta pantalla siga siendo un prototipo,
// solo Isabel Mundo (id 12) debe poder verla, aunque su rol tenga permiso
// sobre el Configurador de Pólizas de Carga Externa. Se compara por id (no
// por username) porque UsuarioIdSerializer no expone el campo username.
const ID_USUARIO_CON_ACCESO = 12;

const PruebaGeneradorPolizasCargaExterna = () => {
  const usuarioActual = useSelector((state) => state?.auth?.user);
  const [loading, setLoading] = useState(false);
  const [conceptos, setConceptos] = useState([]);
  const [cargas, setCargas] = useState([]);
  const [conceptoIds, setConceptoIds] = useState([]);
  const [cargaId, setCargaId] = useState(null);
  const [bloques, setBloques] = useState(null);
  const [comparativo, setComparativo] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      // El listado de cargas viene paginado (PageNumberPagination del ViewSet): se recorren
      // todas las paginas, si no solo aparecerian las cargas de la primera.
      const obtenerTodasLasCargas = async () => {
        const todas = [];
        let page = 1;
        let hayMas = true;
        while (hayMas) {
          // eslint-disable-next-line no-await-in-loop
          const res = await getExternaDescuentos({ page });
          if (Array.isArray(res)) return res;
          todas.push(...(res?.results || []));
          hayMas = !!res?.next;
          page += 1;
        }
        return todas;
      };
      const [_conceptos, _cargas] = await Promise.all([
        getConfiguracionesDePolizasDeCargaExterna({ estados_globales: ESTATUS_AUTORIZADO }),
        obtenerTodasLasCargas(),
      ]);
      if (mounted) {
        setConceptos(_conceptos);
        setCargas([..._cargas].sort((a, b) => b.id - a.id));
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (usuarioActual?.id !== ID_USUARIO_CON_ACCESO) {
    return (
      <Row className="container">
        <Col span={24}>
          <Result
            status="403"
            title="Acceso restringido"
            subTitle="Esta pantalla de prueba temporal solo está disponible para un usuario autorizado."
          />
        </Col>
      </Row>
    );
  }

  const onGenerar = async () => {
    if (!conceptoIds.length || !cargaId) {
      message.warning('Selecciona al menos un Concepto y una Carga Externa.');
      return;
    }
    setLoading(true);
    try {
      const conceptosSeleccionados = conceptoIds
        .map((id) => conceptos.find((c) => c.id === id))
        .filter(Boolean);

      const [detalleRes, patronalVivienda, ...lineasPorConcepto] = await Promise.all([
        API.get(`nominas/descuentos-carga-externa-archivo-empleado-vista-optimized/${cargaId}/`),
        getReporteIPEJALPatronalVivienda(cargaId),
        ...conceptosSeleccionados.map((c) => getPolizasDeCargaExterna({
          configuracion_de_poliza_carga_externa: c.id,
        })),
      ]);

      // Misma tecnica que en el Reporte IPEJAL (CargaExternaDescuentos.jsx): se mapean
      // FONDO DE PENSIONES/FONDOS DE VIVIENDA a PATRONAL/VIVIENDA y se unen a los descuentos,
      // para clasificar todo junto por concepto y por grupo (Plantilla/Seguridad/Eventual).
      const CONCEPTO_IPEJAL_A_NOMBRE_REPORTE = {
        'FONDO DE PENSIONES': 'PATRONAL',
        'FONDOS DE VIVIENDA': 'VIVIENDA',
      };
      const allDetalles = [...(detalleRes?.data || [])];
      allDetalles.push(...(patronalVivienda || [])
        .filter((item) => CONCEPTO_IPEJAL_A_NOMBRE_REPORTE[item.concepto])
        .map((item) => ({
          descripcion_del_concepto_externo: CONCEPTO_IPEJAL_A_NOMBRE_REPORTE[item.concepto],
          importe: item.importe,
          tipo_de_empleado_presupuestal_clave: item.tipo_de_empleado_presupuestal_clave,
        })));

      const groupedData = {};
      const sedarGrouped = {
        'Aportacion Adicional Voluntaria SEDAR': {
          concepto: 'Aportacion Adicional Voluntaria SEDAR', ipejal: 0, plantilla: 0, seguridad: 0, eventual: 0,
        },
        'Aportacion Adicional Complementaria SEDAR': {
          concepto: 'Aportacion Adicional Complementaria SEDAR', ipejal: 0, plantilla: 0, seguridad: 0, eventual: 0,
        },
        'Aportación al SEDAR': {
          concepto: 'Aportación al SEDAR', ipejal: 0, plantilla: 0, seguridad: 0, eventual: 0,
        },
      };

      allDetalles.forEach((item) => {
        const nombreConcepto = item.descripcion_del_concepto_externo || 'SIN CONCEPTO';
        const importe = parseFloat(item.importe) || 0;
        const grupo = clasificarGrupoIPEJAL(item);

        let targetGroup = sedarGrouped[
          Object.keys(sedarGrouped).find((k) => normalize(k) === normalize(nombreConcepto))
        ];
        if (!targetGroup) {
          if (!groupedData[nombreConcepto]) {
            groupedData[nombreConcepto] = {
              concepto: nombreConcepto, ipejal: 0, plantilla: 0, seguridad: 0, eventual: 0,
            };
          }
          targetGroup = groupedData[nombreConcepto];
        }
        targetGroup.ipejal += importe;
        if (grupo) targetGroup[grupo] += importe;
      });

      const upperRows = Object.values(groupedData);
      const lowerRows = Object.values(sedarGrouped);
      setComparativo({ upperRows, lowerRows });

      const buscarFila = (rows, nombre) => rows
        .find((r) => normalize(r.concepto) === normalize(nombre));

      const montoPara = (descripcion, grupo, esSedar) => {
        const d = normalize(descripcion);
        if (d.includes('fondo de pensiones')) return buscarFila(upperRows, 'PATRONAL')?.[grupo] || 0;
        if (d.includes('vivienda')) return buscarFila(upperRows, 'VIVIENDA')?.[grupo] || 0;
        if (d.includes('retencion')) {
          return upperRows
            .filter((r) => !['PATRONAL', 'VIVIENDA'].includes(r.concepto))
            .reduce((acc, r) => acc + (r[grupo] || 0), 0);
        }
        // Poliza SEDAR: solo "Aportación al SEDAR". Las aportaciones adicionales Voluntaria y
        // Complementaria NO entran (ver hoja SEDAR de la ficha 170: APORTACION FONDO SEDAR =
        // renglon "SEDAR" del comparativo, no su TOTAL).
        const aportacionSedar = buscarFila(lowerRows, 'Aportación al SEDAR')?.[grupo] || 0;
        if (d.includes('sedar')) return aportacionSedar;
        if (d.includes('servicios personales')) {
          return esSedar
            ? aportacionSedar
            : upperRows.reduce((acc, r) => acc + (r[grupo] || 0), 0);
        }
        return 0;
      };

      // Un bloque por cada concepto seleccionado y cada grupo al que aplican sus renglones.
      // Los conceptos actuales ya vienen separados por grupo (p. ej. "IPEJAL PERSONAL DE
      // COMISARÍA" solo tiene renglones de seguridad), asi que normalmente es un bloque por
      // concepto; si un concepto solo tiene renglones sin grupo ("Todos"), salen los 3.
      const nuevosBloques = conceptosSeleccionados.flatMap((concepto, idx) => {
        const polizaLineas = lineasPorConcepto[idx] || [];
        const esSedar = normalize(concepto.concepto).includes('sedar');
        const gruposDelConcepto = GRUPOS.filter(
          (g) => polizaLineas.some((l) => l.grupo === g.key),
        );
        return (gruposDelConcepto.length ? gruposDelConcepto : GRUPOS).map((g) => {
          const renglones = polizaLineas
            .filter((l) => l.grupo === null || l.grupo === g.key)
            .map((l) => {
              const monto = Math.round(montoPara(l.descripcion, g.key, esSedar) * 100) / 100;
              return {
                key: l.id,
                scta: l.scta,
                sscta: l.sscta,
                cuenta_completa: l.cuenta_completa,
                descripcion: l.descripcion,
                debe: l.es_cargo ? monto : null,
                haber: l.es_cargo ? null : monto,
              };
            });
          const totalDebe = renglones.reduce((acc, r) => acc + (r.debe || 0), 0);
          const totalHaber = renglones.reduce((acc, r) => acc + (r.haber || 0), 0);
          return {
            key: `${concepto.id}-${g.key}`,
            concepto: concepto.concepto,
            grupo: g.label,
            renglones,
            totalDebe: Math.round(totalDebe * 100) / 100,
            totalHaber: Math.round(totalHaber * 100) / 100,
          };
        });
      });

      setBloques(nuevosBloques);
    } catch (err) {
      onError(err, setLoading);
    }
    setLoading(false);
  };

  const onExportar = async () => {
    if (!bloques || !comparativo) return;
    const carga = cargas.find((c) => c.id === cargaId);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Poliza');
    worksheet.columns = [
      { header: '', key: 'scta', width: 12 },
      { header: '', key: 'sscta', width: 12 },
      { header: '', key: 'cuenta', width: 28 },
      { header: '', key: 'descripcion', width: 45 },
      { header: '', key: 'debe', width: 18 },
      { header: '', key: 'haber', width: 18 },
      { header: '', key: 'espacio', width: 4 },
      { header: '', key: 'comp_concepto', width: 32 },
      { header: '', key: 'comp_ipejal', width: 16 },
      { header: '', key: 'comp_plantilla', width: 16 },
      { header: '', key: 'comp_seguridad', width: 16 },
      { header: '', key: 'comp_eventual', width: 16 },
      { header: '', key: 'comp_diferencia', width: 16 },
    ];

    const r2 = (n) => Math.round((n || 0) * 100) / 100;
    const setCell = (row, col, value, { bold = false, numFmt = null, color = null } = {}) => {
      const cell = worksheet.getRow(row).getCell(col);
      cell.value = value;
      if (bold || color) cell.font = { bold, ...(color ? { color: { argb: color } } : {}) };
      if (numFmt) cell.numFmt = numFmt;
      return cell;
    };

    // --- Lado izquierdo (A-F): bloques de poliza por grupo ---
    let fila = 1;
    bloques.forEach((bloque) => {
      setCell(fila, 1, `POLIZA ${bloque.concepto?.toUpperCase()} - ${carga?.archivo_nombre || ''}, PERSONAL ${bloque.grupo}`, { bold: true, color: 'FFC55A11' });
      fila += 1;
      ['SCTA', 'SSCTA', 'CUENTA', 'DESCRIPCION', 'DEBE', 'HABER'].forEach((h, idx) => setCell(fila, idx + 1, h, { bold: true }));
      fila += 1;
      bloque.renglones.forEach((r) => {
        setCell(fila, 1, r.scta);
        setCell(fila, 2, r.sscta);
        setCell(fila, 3, r.cuenta_completa);
        setCell(fila, 4, r.descripcion);
        // ?? (no ||): un monto 0 se exporta como 0.00 en vez de quedar en blanco.
        setCell(fila, 5, r.debe ?? '', { numFmt: '#,##0.00' });
        setCell(fila, 6, r.haber ?? '', { numFmt: '#,##0.00' });
        fila += 1;
      });
      setCell(fila, 4, 'TOTAL', { bold: true });
      setCell(fila, 5, bloque.totalDebe, { bold: true, numFmt: '#,##0.00' });
      setCell(fila, 6, bloque.totalHaber, { bold: true, numFmt: '#,##0.00' });
      fila += 2;
    });

    // --- Lado derecho (H en adelante): tabla comparativa completa, igual que el Reporte IPEJAL ---
    const { upperRows, lowerRows } = comparativo;
    let filaComp = 1;
    setCell(filaComp, 9, 'COMPARACIÓN', { bold: true });
    filaComp += 1;
    ['CONCEPTOS', 'IPEJAL', 'PLANTILLA', 'SEGURIDAD', 'EVENTUAL', 'DIFERENCIA'].forEach((h, idx) => setCell(filaComp, 8 + idx, h, { bold: true }));
    filaComp += 1;

    const escribirFilaComparativa = (r) => {
      const diferencia = r2(r.plantilla + r.seguridad + r.eventual - r.ipejal);
      setCell(filaComp, 8, r.concepto);
      setCell(filaComp, 9, r2(r.ipejal), { numFmt: '#,##0.00' });
      setCell(filaComp, 10, r2(r.plantilla), { numFmt: '#,##0.00' });
      setCell(filaComp, 11, r2(r.seguridad), { numFmt: '#,##0.00' });
      setCell(filaComp, 12, r2(r.eventual), { numFmt: '#,##0.00' });
      setCell(filaComp, 13, diferencia, { numFmt: '#,##0.00' });
      filaComp += 1;
    };

    upperRows.forEach(escribirFilaComparativa);
    const totalUpper = upperRows.reduce((acc, r) => ({
      ipejal: acc.ipejal + r.ipejal,
      plantilla: acc.plantilla + r.plantilla,
      seguridad: acc.seguridad + r.seguridad,
      eventual: acc.eventual + r.eventual,
    }), {
      ipejal: 0, plantilla: 0, seguridad: 0, eventual: 0,
    });
    escribirFilaComparativa({ concepto: 'TOTAL GENERAL', ...totalUpper });
    filaComp += 1;

    lowerRows.forEach(escribirFilaComparativa);
    const totalLower = lowerRows.reduce((acc, r) => ({
      ipejal: acc.ipejal + r.ipejal,
      plantilla: acc.plantilla + r.plantilla,
      seguridad: acc.seguridad + r.seguridad,
      eventual: acc.eventual + r.eventual,
    }), {
      ipejal: 0, plantilla: 0, seguridad: 0, eventual: 0,
    });
    escribirFilaComparativa({ concepto: 'TOTAL SEDAR', ...totalLower });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const conceptosEnPoliza = [...new Set(bloques.map((b) => b.concepto))];
    link.download = `PRUEBA_POLIZA_${conceptosEnPoliza.length === 1 ? conceptosEnPoliza[0] : 'VARIOS_CONCEPTOS'}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columnas = [
    { title: 'SCTA', dataIndex: 'scta', key: 'scta' },
    { title: 'SSCTA', dataIndex: 'sscta', key: 'sscta' },
    { title: 'Cuenta Completa', dataIndex: 'cuenta_completa', key: 'cuenta_completa' },
    { title: 'Descripción', dataIndex: 'descripcion', key: 'descripcion' },
    {
      title: 'Debe',
      dataIndex: 'debe',
      key: 'debe',
      align: 'right',
      render: (v) => (v != null ? currencyFormatter(v) : ''),
    },
    {
      title: 'Haber',
      dataIndex: 'haber',
      key: 'haber',
      align: 'right',
      render: (v) => (v != null ? currencyFormatter(v) : ''),
    },
  ];

  return (
    <Row className="container">
      <Col span={24}>
        <Title level={4}>PRUEBA — Generador de Pólizas de Carga Externa (borrar después)</Title>
        <Spin spinning={loading}>
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Text>Concepto de Póliza</Text>
              <Select
                dataSource={conceptos}
                labelProp="concepto"
                valueProp="id"
                mode="multiple"
                value={conceptoIds}
                onChange={(value) => setConceptoIds(value || [])}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Text>Carga Externa</Text>
              <Select
                dataSource={cargas}
                render={(item) => `${item.id} - ${item.archivo_nombre}`}
                valueProp="id"
                value={cargaId}
                onChange={setCargaId}
              />
            </Col>
            <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <Button type="primary" onClick={onGenerar}>Generar</Button>
              <Button onClick={onExportar} disabled={!bloques}>Exportar a Excel</Button>
            </Col>
          </Row>

          {bloques && bloques.map((bloque) => (
            <Card
              key={bloque.key}
              title={`${bloque.concepto} — PERSONAL ${bloque.grupo}`}
              style={{ marginBottom: 16 }}
            >
              <AntTable
                columns={columnas}
                dataSource={bloque.renglones}
                pagination={false}
                size="small"
                summary={() => (
                  <AntTable.Summary.Row>
                    <AntTable.Summary.Cell index={0} colSpan={4}>
                      <strong>
                        TOTAL (diferencia:
                        {currencyFormatter(bloque.totalDebe - bloque.totalHaber)}
                        )
                      </strong>
                    </AntTable.Summary.Cell>
                    <AntTable.Summary.Cell index={1} align="right">
                      <strong>{currencyFormatter(bloque.totalDebe)}</strong>
                    </AntTable.Summary.Cell>
                    <AntTable.Summary.Cell index={2} align="right">
                      <strong>{currencyFormatter(bloque.totalHaber)}</strong>
                    </AntTable.Summary.Cell>
                  </AntTable.Summary.Row>
                )}
              />
            </Card>
          ))}
        </Spin>
      </Col>
    </Row>
  );
};

export default PruebaGeneradorPolizasCargaExterna;
